import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { OrderItemRowTypeEnumResponseDto, OrderPropertyEnumRequestDto } from '@core/services/apiclient';
import { CellPreparedEvent } from 'devextreme/ui/tree_list';
import CheckBox from 'devextreme/ui/check_box';
import { OrdersTreeListUtilsService } from './orders-tree-utils.service';

@Injectable()
export class OrdersCellRenderingService {
  private readonly ordersUtils = inject(OrdersTreeListUtilsService);
  private readonly rendererFactory = inject(RendererFactory2);
  private renderer: Renderer2;

  private visibleRows: any[] = [];
  private indexByKey = new Map<any, number>();
  private isSelectAllListenerAttached = false;

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  getVisibleRows(): any[] {
    return this.visibleRows;
  }

  processCellPrepared(
    event: CellPreparedEvent,
    onSelectAllCheckboxChange: (value: boolean | undefined) => void,
  ): void {
    if (event?.rowType === 'data') {
      if (event.columnIndex === 0) {
        this.rebuildVisibleIndex(event.component);
        this.addChildRowClass(event);
        this.addMergedRowClass(event);
      }

      this.addProductRowClass(event);
      this.hideSameColumnValue(event, OrderPropertyEnumRequestDto.OrderNumber);

      if (event.column.dataField === OrderPropertyEnumRequestDto.OrderTotalAmount) {
        this.addTotalAmountCellClass(event);
      }
    }

    if (event.rowType === 'header' && event.columnIndex === 0) {
      this.listenToSelectAllCheckbox(event, onSelectAllCheckboxChange);
    }
  }

  private rebuildVisibleIndex(component: any): void {
    this.visibleRows = component.getVisibleRows() || [];
    this.indexByKey.clear();
    for (let i = 0; i < this.visibleRows.length; i++) {
      const key = this.visibleRows[i]?.key;
      if (key !== undefined) {
        this.indexByKey.set(key, i);
      }
    }
  }

  private addChildRowClass(event: CellPreparedEvent): void {
    if (event?.data?.parentId) {
      const rowElement = event?.component?.getRowElement(event?.rowIndex)?.[0];
      if (rowElement && !rowElement.classList.contains('child-row')) {
        this.renderer.addClass(rowElement, 'child-row');
      }
    }
  }

  private addProductRowClass(event: CellPreparedEvent): void {
    if (event?.data?.orderItemRowType === OrderItemRowTypeEnumResponseDto.Product) {
      this.renderer.addClass(event.cellElement, 'orders-product-row');
    }
  }

  private hideSameColumnValue(event: CellPreparedEvent, column: string): void {
    let idx = this.indexByKey.get(event?.key);
    if (idx === undefined) {
      this.rebuildVisibleIndex(event.component);
      idx = this.indexByKey.get(event?.key);
    }
    if (idx === undefined) return;

    const current = this.visibleRows[idx]?.data;
    const previous = this.visibleRows[idx - 1]?.data;

    if (event?.column?.dataField === column) {
      if (previous && current?.orderId === previous?.orderId) {
        if (current?.parentId != null) {
          this.renderer.addClass(event.cellElement, 'hidden-child-cell');
          return;
        }
        this.renderer.addClass(event.cellElement, 'hidden-order-id-cell');
      }
    }
  }

  private addMergedRowClass(event: CellPreparedEvent): void {
    const idx = this.indexByKey.get(event?.key);
    if (idx === undefined) return;

    const rowElement = event?.component?.getRowElement(event?.rowIndex)?.[0];
    if (!rowElement) return;

    if (this.ordersUtils.sameAsNextByIdx(idx, this.visibleRows)) {
      this.renderer.addClass(rowElement, 'merged-row');
    } else {
      this.renderer.removeClass(rowElement, 'merged-row');
    }
  }

  private calculateTotalAmountRowSpan(event: CellPreparedEvent): number {
    const currentOrderId = this.visibleRows[event.rowIndex]?.data?.orderId;
    if (!currentOrderId) return 1;

    let rowSpan = 1;
    let nextIndex = event.rowIndex + 1;

    while (
      nextIndex < this.visibleRows.length &&
      this.visibleRows[nextIndex]?.data?.orderId === currentOrderId
    ) {
      rowSpan++;
      nextIndex++;
    }

    return rowSpan;
  }

  private addTotalAmountCellClass(event: CellPreparedEvent): void {
    if (event?.column?.dataField !== OrderPropertyEnumRequestDto.OrderTotalAmount) return;

    const idx = this.indexByKey.get(event?.key);
    if (idx === undefined) return;

    const isFirst = this.ordersUtils.isFirstOfGroupByIdx(idx, this.visibleRows);
    if (!isFirst) {
      event.cellElement.remove();
      return;
    }

    this.renderer.removeClass(event.cellElement, 'hidden-order-id-cell');
    this.renderer.addClass(event.cellElement, 'total-amount-cell');
    const rowSpan = this.calculateTotalAmountRowSpan(event);
    this.renderer.setAttribute(event.cellElement, 'rowspan', `${rowSpan}`);
  }

  private listenToSelectAllCheckbox(
    event: CellPreparedEvent,
    onSelectAllCheckboxChange: (value: boolean | undefined) => void,
  ): void {
    if (this.isSelectAllListenerAttached) return;
    const checkboxEl = event.cellElement.querySelector('.dx-select-checkbox') as HTMLElement;
    if (checkboxEl) {
      const selectAllCheckbox = CheckBox.getInstance(checkboxEl);
      if (selectAllCheckbox) {
        selectAllCheckbox.on('valueChanged', (e: any) => {
          const isUserEvent = Boolean(e?.event);
          if (!isUserEvent) return;

          const target = e?.event?.target as Node | null | undefined;
          if (!target || !checkboxEl.contains(target)) return;

          const rawValue = e?.value as boolean | null | undefined;
          const nextValue = rawValue == null ? undefined : rawValue;
          queueMicrotask(() => onSelectAllCheckboxChange(nextValue));
        });
        this.isSelectAllListenerAttached = true;
      }
    }
  }
}
